import { queryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { useAppSelector } from 'redux/hooks';
import { removeUrlParams, createUrl } from '@doda/common/src/utils/urlUtil';
import { MouseEvent } from 'react';
import { makeStaticUrl } from '@doda/common/src/logics/staticUrl';
import { QUERY_PARAMS } from '@doda/common/src/constants/queryParams';
import { useRouter } from 'next/router';
import {
  PATH_NAME_ACTION,
  REMOVE_PARAM,
  REMOVE_PARAM_LIST,
} from '../../constants';

/**
 * カノニカル形式もしくは動的URLのベースとなるURLを生成するカスタムフック
 * @param {Object} params
 * @param {string} params.baseUrl - ベースとなるURL
 * @param {Record<string, string>} [params.picAdditionalParam] - 画像あり押下時の解析系等の追加クエリパラメータ（任意）
 * @param {Record<string, string>} [params.noPicAdditionalParam] - 画像無し押下時の解析系等の追加クエリパラメータ（任意）
 * @param {boolean} [params.isStaticUrl=false] - 静的URLを使用するかどうか（任意）
 * @returns {Object} 画像ありとなしのURLおよびクリックハンドラー (widthImageUrl: 画像ありのURL, withoutImageUrl: 画像なしのURL, onClickPic: アイコンクリック時のハンドラー)
 */
export const useCreatePic = ({
  baseUrl,
  picAdditionalParam,
  noPicAdditionalParam,
  isStaticUrl = false,
}: {
  baseUrl: string;
  picAdditionalParam?: Record<string, string>;
  noPicAdditionalParam?: Record<string, string>;
  isStaticUrl?: boolean;
}) => {
  const { pathname } = useRouter();
  const REMOVE_PARAMS_FOR_PIC = [...REMOVE_PARAM_LIST, 'prsrt'];
  const queryParams = useAppSelector(queryParamsSelector);
  const { pic = '1', page } = queryParams;
  const removedUrlForPic = removeUrlParams(baseUrl, REMOVE_PARAMS_FOR_PIC);
  const hasImage = pic === '1';
  let widthImageUrl: string | undefined;
  let withoutImageUrl: string | undefined;

  // urlが正規化されている場合
  if (isStaticUrl) {
    // 画像ありアイコンのURL
    widthImageUrl = hasImage
      ? undefined
      : createUrl({
          href: makeStaticUrl(
            {
              ...queryParams,
              ...REMOVE_PARAM,
              [QUERY_PARAMS.pic]: '1',
              [QUERY_PARAMS.page]: page,
            },
            pathname + PATH_NAME_ACTION
          ),
          params: {
            ...picAdditionalParam,
          },
        });
    // 画像なしアイコンのURL
    withoutImageUrl = !hasImage
      ? undefined
      : createUrl({
          href: makeStaticUrl(
            {
              ...queryParams,
              ...REMOVE_PARAM,
              [QUERY_PARAMS.pic]: '0',
              [QUERY_PARAMS.page]: page,
            },
            pathname + PATH_NAME_ACTION
          ),
          params: {
            ...noPicAdditionalParam,
          },
        });
  } else {
    // 画像ありアイコンのURL
    widthImageUrl = hasImage
      ? undefined // 画像ありが既に選択されていれば、URLは無し
      : createUrl({
          href: createUrl({
            href: removedUrlForPic,
            params: { pic: '1' },
          }),
          params: { ...picAdditionalParam },
        });

    // 画像なしアイコンのURL
    withoutImageUrl = !hasImage
      ? undefined // 画像なしが既に選択されていれば、URLは無し
      : createUrl({
          href: createUrl({
            href: removedUrlForPic,
            params: { pic: '0' },
          }),
          params: { ...noPicAdditionalParam },
        });
  }

  /**
   * リンク押下時に正規化したURLが動的に変わってしまうのでイベントで制御
   * @param {MouseEvent<HTMLAnchorElement>} event - クリックイベント
   * @returns {void}
   */
  const onClickPic = (event: MouseEvent<HTMLAnchorElement>) => {
    // 正規化されていない場合は処理終了
    if (!isStaticUrl) return;
    if (widthImageUrl) {
      event.currentTarget.href = widthImageUrl;
    }

    if (withoutImageUrl) {
      event.currentTarget.href = withoutImageUrl;
    }
  };

  return { widthImageUrl, withoutImageUrl, onClickPic };
};
